import axios from 'axios'
import { AUTH_USER_KEY, loginPathWithReturn } from './authRedirect'
import { clearGrantMirror, readGrantMirror } from './grantMirror'
import { isNativeApp } from './native/platform'
import { apiBaseUrl, getInstance } from './native/instance'
import { bearerFor, credentialKindFor } from './native/bearer'
import { dropRejectedGrant, isWipeAckUrl, rejectedGrantForWipeAck, retainRejectedGrant } from './native/wipeAckCredential'
import { clearCredential, getCredential, type CredentialKind } from './native/credentialStore'
import { wipeOfflineData } from './offlineStore'

// [CANV-2] Defense-in-depth: a global request timeout. Without one, a never-settling request
// (a backend GET that stalls mid-response) spins the UI forever — no error, no retry path — which
// is the failure class behind the canvasser turf-list hang. A finite default turns any hang into a
// rejected promise React Query surfaces as a retryable error. 60s clears the 30s OSRM ceiling
// (client compute-route calls can take that long) with headroom, so a slow-but-healthy request on a
// weak mobile connection isn't false-positived. File uploads (uploadFile) override this to 0 (no
// timeout), since a large inline CSV import can legitimately run longer than any interactive call.
const API_TIMEOUT_MS = 60_000

// Web: same-origin '/api' + HttpOnly cookies. Native B1 (NB1-8): the request interceptor below
// re-points each request at the selected instance and swaps cookies for a bearer token.
const api = axios.create({
  baseURL: '/api',
  withCredentials: true, // Send HttpOnly auth cookie with every request (web; off per-request on native)
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Pure classifier for a 401's request URL, split out of the interceptor below
 * so it's unit-testable without stubbing axios/window. Two independent auth
 * contexts share this axios instance:
 *  - 'grant': a canvasser data 401 (/api/grant/*, /api/canvassing/*) mid-session —
 *    gated server-side by the GrantOnly policy; an expired/revoked session here bounces to sign-in.
 *  - 'grant-probe': the /api/grant/me identity probe specifically. It legitimately 401s for a
 *    not-yet-signed-in visitor on a PUBLIC surface (the /join enroll funnel, the front-door chooser),
 *    which fire it to decide what to render. Clearing the stale ids-only mirror is fine, but it must
 *    NOT force-navigate — doing so kicked a first-time canvasser off /join before they could enroll.
 *    The in-app expired-session case is already handled by RequireGrant's own <Navigate>.
 *  - 'auth': the organizer/admin JWT session (everything else).
 *  - 'none': the login request itself — never bounce off a failed login POST.
 */
export function resolve401Target(url: string | undefined): 'grant' | 'grant-probe' | 'auth' | 'none' {
  const requestUrl = url ?? ''
  if (requestUrl.includes('/grant/me')) return 'grant-probe'
  if (requestUrl.includes('/grant') || requestUrl.includes('/canvassing')) return 'grant'
  if (requestUrl.includes('/auth/login')) return 'none'
  return 'auth'
}

/**
 * [CANV-6] True when the current page is a canvasser surface. Kept out of resolve401Target (which
 * stays a pure URL classifier) so the interceptor can layer context on top: a 401 in canvasser
 * context must land on the canvasser redeem screen, never the organizer /login. Signals: the
 * /canvasser path prefix (the primary one), or a persisted grant mirror (an active canvasser whose
 * path is momentarily elsewhere). The /join enroll funnel is deliberately NOT included — a
 * first-time visitor there has no session and its only auth call is the /grant/me probe, already
 * handled as 'grant-probe' (which must not navigate).
 */
export function isCanvasserContext(): boolean {
  try {
    if (window.location.pathname.startsWith('/canvasser')) return true
  } catch {
    // window.location can be unavailable in non-browser contexts; fall through to the mirror check.
  }
  // [Canvass this, CT-11] An organizer (auth_user present) holding a set-aside grant mirror on a
  // non-/canvasser page is still operating as an organizer.
  return !!readGrantMirror() && !hasAuthUser()
}

function hasAuthUser(): boolean {
  try {
    return !!localStorage.getItem(AUTH_USER_KEY)
  } catch {
    return false
  }
}

// Response interceptor: on 401 (missing/expired auth cookie or revoked grant
// session), drop the stale local session and bounce to the right sign-in
// surface, preserving where the user was. This is the second line of defence
// behind the RequireAuth/RequireGrant route guards — it catches the case
// where localStorage/mirror still has a session but the cookie has expired.
/** The bearer token a request actually carried (native), or null. */
function presentedBearer(config: { headers?: unknown } | undefined): string | null {
  const h = config?.headers as { get?: (k: string) => unknown; Authorization?: unknown } | undefined
  const v = typeof h?.get === 'function' ? h.get('Authorization') : h?.Authorization
  return typeof v === 'string' && v.startsWith('Bearer ') ? v.slice('Bearer '.length) : null
}

/**
 * The 401 target with the [CANV-6] context override applied: a 401 while the user is operating as
 * a canvasser must NEVER route to the organizer email/password /login. The URL classifier defaults
 * any non-grant/non-canvassing endpoint to 'auth' (organizer), but a canvasser surface can
 * legitimately (or, by regression, accidentally) call such an endpoint — the culprit was the
 * organizer-only /contacts/{id}/detail. On a /canvasser path (or holding a grant mirror), an 'auth'
 * 401 is treated as a grant 401 so it lands on the canvasser redeem screen. The grant-probe case is
 * left untouched (it must not navigate at all — see resolve401Target). Computed ONCE per 401 and
 * shared by the native credential clear and the routing, so the two can never disagree.
 */
export function effective401Target(url: string | undefined): ReturnType<typeof resolve401Target> {
  const target = resolve401Target(url)
  return target === 'auth' && isCanvasserContext() ? 'grant' : target
}

/**
 * [Native B1, WI-17] Drop the native credential a 401 just proved dead, BEFORE the existing
 * routing runs. The 401 implicates ONE stored credential: the grant one for a 'grant'/'grant-probe'
 * target (incl. the CANV-6 override), and for a plain 'auth' target whichever family the URL
 * belongs to (so a dead attendee `gs_` on /lobby/attendee* is the grant credential, M4). It is
 * cleared only when the bearer the request ACTUALLY carried is that stored credential (R13): a
 * fallback bearer (NB1-8) sent to the other family's endpoint, or a token re-issued while the
 * request was in flight, is not evidence against the stored one. Return credentials are never
 * cleared here (expiry preserves them; only logout and RedeemPage-404 wipe). No re-issue: the
 * user re-authenticates.
 *
 * [LEGAL] A cleared grant credential is kept in memory for the signed-out landing's wipe-ack
 * only (wipeAckCredential.ts), so a server-driven revoke still stamps WipeConfirmedAt.
 */
async function clearRejectedNativeCredential(
  target: ReturnType<typeof resolve401Target>,
  config: { url?: string; headers?: unknown } | undefined,
): Promise<void> {
  const kind: CredentialKind | null =
    target === 'grant' || target === 'grant-probe'
      ? 'grant'
      : target === 'auth'
        ? credentialKindFor(config?.url)
        : null
  if (!kind) return
  const presented = presentedBearer(config)
  if (presented === null || presented !== getCredential(kind)) return
  await clearCredential(kind)
  if (kind === 'grant') retainRejectedGrant(presented)
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status !== 401) return handle401(error, 'none')
    const target = effective401Target(error.config?.url)
    if (isNativeApp()) return clearRejectedNativeCredential(target, error.config).then(() => handle401(error, target))
    return handle401(error, target)
  }
)

// The existing web routing, unchanged; on native it runs after the credential clear above.
// `target` is effective401Target(url) (CANV-6 override included), computed once by the caller.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the axios error, passed through untouched
function handle401(error: any, target: ReturnType<typeof resolve401Target>): Promise<never> {
  if (error.response?.status === 401) {
    if (target === 'grant' || target === 'grant-probe') {
      // [LEGAL] Do NOT wipe offline data here — that's the logout/revoke-wipe
      // path. This just clears the ids-only mirror. (Sole exception: the native pre-reload wipe
      // below, which runs only when already committed to the signed-out landing that wipes anyway.)
      clearGrantMirror()
      // [Canvass this, CT-11] An organizer's set-aside grant 401s off a /canvasser path: stay put.
      // /canvasser/redeem-needed wipes the offline queue on mount, which would destroy the set-aside
      // visits. Every other case below is unchanged.
      if (target === 'grant' && !window.location.pathname.startsWith('/canvasser') && hasAuthUser()) {
        return Promise.reject(error)
      }
      // Only a real data 401 ('grant') forces the signed-out screen. The 'grant-probe' (/grant/me)
      // case must NOT navigate: it fires on the PUBLIC /join funnel for a first-time visitor, and
      // the in-app expired case is already redirected by RequireGrant. See resolve401Target.
      if (target === 'grant' && window.location.pathname !== '/canvasser/redeem-needed') {
        // [Native B1, N1, LEGAL] On native location.replace() reloads the WebView, which drops the
        // in-memory rejected gs_ before the landing page could send its wipe-ack. So the landing's
        // wipe + ack run HERE, before the reload, while the value is still held (one awaited,
        // bounded sequence — never persisted). The landing re-wipes on mount (idempotent).
        // (Skipped when the 401 IS the wipe-ack, which would otherwise wait on its own sequence.)
        if (isNativeApp() && !isWipeAckUrl(error.config?.url)) {
          return wipeAndAckBeforeReload().then(() => {
            window.location.replace('/canvasser/redeem-needed')
            return Promise.reject(error)
          })
        }
        window.location.replace('/canvasser/redeem-needed')
      }
    } else if (target === 'auth') {
      const alreadyOnLogin = window.location.pathname === '/login'
      if (!alreadyOnLogin) {
        localStorage.removeItem(AUTH_USER_KEY)
        const loginTarget = loginPathWithReturn(window.location.pathname + window.location.search)
        // replace() so the dead, un-authenticated page isn't left in history.
        window.location.replace(loginTarget)
      }
    }
    // target === 'none': the login request itself failed — do nothing, let
    // the caller (LoginPage) handle the rejected promise.
  }
  return Promise.reject(error)
}

/** Bound on the native pre-reload wipe + ack: a hung wipe or offline ack must not strand the user on a dead page. */
export const NATIVE_WIPE_BEFORE_RELOAD_MS = 5000
let wipeBeforeReload: Promise<void> | null = null

/**
 * [Native B1, N1, LEGAL] The signed-out landing's wipe → wipe-ack, run by the 401 interceptor before
 * the native reload (see handle401). The ack goes only after a successful wipe (as on the landing)
 * and picks up the retained rejected gs_ via bearerFor; the value is dropped once this settles.
 * Single-flight, so concurrent 401s share one wipe and one ack.
 */
function wipeAndAckBeforeReload(): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  wipeBeforeReload ??= Promise.race([
    wipeOfflineData().then(() => grantApi.wipeAck().then(() => {}, () => {}), () => {}),
    new Promise<void>((resolve) => { timer = setTimeout(resolve, NATIVE_WIPE_BEFORE_RELOAD_MS) }),
  ]).finally(() => {
    clearTimeout(timer)
    dropRejectedGrant()
    wipeBeforeReload = null
  })
  return wipeBeforeReload
}

// Request interceptor: attach committee scope when active
import { getCommitteeScope } from './committeeScope'

api.interceptors.request.use((config) => {
  const scope = getCommitteeScope()
  if (scope) {
    config.params = { ...config.params, committeeScope: scope }
  }
  // [Native B1, NB1-8] Re-read the instance per request so an organization switch takes effect
  // without a reload; no cookies; the bearer follows the URL family, never the active mode.
  if (isNativeApp()) {
    config.withCredentials = false
    // [M2] An absolute or protocol-relative URL bypasses baseURL (axios), so it is NOT the
    // instance: it never gets a bearer. (The org-switch existence probe relies on this.)
    if (isAbsoluteOrProtocolRelative(config.url)) return config
    // Fail closed: with no instance a relative URL would resolve against the app shell, which may
    // answer index.html with a 200. Nothing is sent.
    if (!getInstance()) {
      return Promise.reject(new axios.AxiosError('No instance selected', 'ERR_NATIVE_NO_INSTANCE', config))
    }
    config.baseURL = apiBaseUrl()
    const token = bearerFor(config.url)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      // [LEGAL] The retained rejected gs_ rides exactly one wipe-ack, then it is gone.
      if (isWipeAckUrl(config.url) && token === rejectedGrantForWipeAck()) dropRejectedGrant()
    }
  }
  return config
})

/** `scheme:…`, or a leading pair of slashes/backslashes (`//host`) — anything axios or a browser would not resolve under baseURL. */
export function isAbsoluteOrProtocolRelative(url: string | undefined): boolean {
  const u = (url ?? '').trimStart()
  return /^[a-z][a-z\d+\-.]*:/i.test(u) || /^[\\/]{2}/.test(u)
}

export { api }

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Browser's UTC offset in minutes (e.g. 300 for EST). Passed to time-sensitive endpoints. */
const getTzOffset = () => new Date().getTimezoneOffset()

/** Merge tzOffset into a params object. Accepts optional override (used by canvasser mobile). */
const withTzOffset = <T extends Record<string, unknown>>(params?: T, tzOffset?: number) =>
  ({ ...params, tzOffset: tzOffset ?? getTzOffset() })

/** POST a single file as multipart/form-data. */
const uploadFile = <T>(url: string, fieldName: string, file: File) => {
  const form = new FormData()
  form.append(fieldName, file)
  // timeout: 0 exempts uploads from the global API_TIMEOUT_MS — a large inline CSV import can run
  // longer than any interactive request, and cutting it off at 60s would corrupt a legit import.
  return api.post<T>(url, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 0,
  })
}

/** Standard getAll / getById / create / update / delete for a REST resource. */
function createCrudApi<Summary, Detail, Save>(basePath: string) {
  return {
    getAll: () =>
      api.get<Summary[]>(basePath),
    getById: (id: string) =>
      api.get<Detail>(`${basePath}/${id}`),
    create: (data: Save) =>
      api.post<Detail>(basePath, data),
    update: (id: string, data: Save) =>
      api.put<Detail>(`${basePath}/${id}`, data),
    delete: (id: string) =>
      api.delete(`${basePath}/${id}`),
  }
}

// ---------------------------------------------------------------------------
// Config API
// ---------------------------------------------------------------------------
import type { ClientConfig, AvailableState, AvailableDistrictsResponse, Limits } from '../types/config'

export const configApi = {
  get: () => api.get<ClientConfig>('/config'),
  // The locality picker chokepoint (see vault "Customer Locality Scoping"): all states + DC
  // today, a customer-scoped subset once instance data-access config lands.
  getAvailableStates: () => api.get<AvailableState[]>('/config/available-states'),
  getAvailableDistricts: (state: string, actingCommitteeId?: string, includeOptions?: boolean) =>
    api.get<AvailableDistrictsResponse>('/config/available-districts', {
      params: { state, actingCommitteeId, includeOptions },
    }),
  // SC-4: the caps this box enforces. Pre-flight messaging only -- the server cap is the gate.
  getLimits: () => api.get<Limits>('/config/limits'),
}

// ---------------------------------------------------------------------------
// Scope Admin API (SuperAdmin-only: product tier + per-committee locality scope)
// ---------------------------------------------------------------------------
import type {
  InstanceConfigDto,
  SetInstanceConfigRequest,
  CommitteeScopeConfigDto,
  ScopeGrantWriteRequest,
  ScopeDefaultsWriteRequest,
  DistrictCatalogResponse,
} from '../types/scoping'

export const scopeAdminApi = {
  getInstanceConfig: () => api.get<InstanceConfigDto>('/admin/instance-config'),

  setInstanceConfig: (data: SetInstanceConfigRequest) =>
    api.put<InstanceConfigDto>('/admin/instance-config', data),

  // The backend returns 204 No Content when the committee has no scope config yet (axios's
  // default validateStatus already treats 204 as success). Normalize that to null here so
  // every caller gets "no config -> empty form" instead of special-casing the HTTP status.
  getCommitteeScope: async (committeeId: string): Promise<CommitteeScopeConfigDto | null> => {
    const r = await api.get<CommitteeScopeConfigDto>(`/admin/committees/${committeeId}/scope`)
    return r.status === 204 ? null : r.data
  },

  setCommitteeGrant: (committeeId: string, data: ScopeGrantWriteRequest) =>
    api.put<CommitteeScopeConfigDto>(`/admin/committees/${committeeId}/scope/grant`, data),

  setCommitteeDefaults: (committeeId: string, data: ScopeDefaultsWriteRequest) =>
    api.put<CommitteeScopeConfigDto>(`/admin/committees/${committeeId}/scope/defaults`, data),

  // Task 7's DISTINCT verbatim district-code catalog for a state, split by chamber -- feeds the
  // Task 8 grant-editor district-code dropdown so a SuperAdmin picks a real code instead of
  // free-texting one that doesn't exist in the voter pool.
  getDistrictCatalog: (state: string) =>
    api.get<DistrictCatalogResponse>('/admin/districts/catalog', { params: { state } }),
}

// ---------------------------------------------------------------------------
// Committee API
// ---------------------------------------------------------------------------

export interface CommitteeHierarchyNode {
  id: string
  label: string
  region?: string
  children: CommitteeHierarchyNode[]
}

export const committeeApi = {
  getMyHierarchy: () =>
    api.get<{ committee: { id: string; label: string; region?: string }; children: CommitteeHierarchyNode[] }>('/committees/my-hierarchy'),
}

// ---------------------------------------------------------------------------
// Contact Search API
// ---------------------------------------------------------------------------
import type {
  ContactSearchRequest,
  ContactSearchResult,
  ContactSearchOptions,
  PaginatedResponse,
} from '../types/contact'

export const contactSearchApi = {
  search: (params: ContactSearchRequest) =>
    api.get<PaginatedResponse<ContactSearchResult>>('/contacts/search', {
      params,
      // Repeat keys (employerTypes=a&employerTypes=b) so ASP.NET binds List<string>/List<Guid>.
      // Also fixes multi-value includeTagIds/excludeTagIds, which share this call.
      paramsSerializer: { indexes: null },
    }),

  getOptions: () =>
    api.get<ContactSearchOptions>('/contacts/search/options'),

  getById: (id: string) =>
    api.get<ContactSearchResult>(`/contacts/${id}`),
}

// ---------------------------------------------------------------------------
// Contact List API
// ---------------------------------------------------------------------------
import type {
  SaveContactListRequest,
  FilterEvaluationRequest,
  ContactListSummary,
  ContactListDetail,
  FilterCountResult,
  ContactPreview,
  FilterFieldMetadata,
  TagOption,
  ContactLocation,
  FilterGroup,
  ListOverview,
  UpdateNotesRequest as UpdateListNotesRequest,
} from '../types/contactList'
import type { ContactWithEmployment } from '../types/workplaceOverlay'
import type { GuidedListSpec } from '../types/guidedList'
// JobEnqueuedDto import lives further down (turfMapApi section) -- import statements are
// hoisted, so it's in scope here too; avoids a duplicate-identifier collision.

export const contactListApi = {
  ...createCrudApi<ContactListSummary, ContactListDetail, SaveContactListRequest>('/lists'),

  getCount: (data: FilterEvaluationRequest) =>
    api.post<FilterCountResult>('/lists/evaluate/count', data),

  getPreview: (data: FilterEvaluationRequest) =>
    api.post<ContactPreview[]>('/lists/evaluate/preview', data),

  materialize: (id: string) =>
    api.post<JobEnqueuedDto>(`/lists/${id}/materialize`),

  getFieldMetadata: () =>
    api.get<FilterFieldMetadata[]>('/lists/field-metadata'),

  getTags: () =>
    api.get<TagOption[]>('/lists/tags'),

  getContactsWithLocations: (id: string) =>
    api.get<ContactLocation[]>(`/lists/${id}/contacts-with-locations`),

  getContactsWithEmployment: (id: string) =>
    api.get<ContactWithEmployment[]>(`/lists/${id}/contacts-with-employment`),

  getOverview: (id: string) =>
    api.get<ListOverview>(`/lists/${id}/overview`),

  updateNotes: (id: string, data: UpdateListNotesRequest) =>
    api.put(`/lists/${id}/notes`, data),

  compileGuided: (spec: GuidedListSpec) =>
    api.post<{ filterGroups: FilterGroup[] }>('/lists/guided/compile', spec),
}

// ---------------------------------------------------------------------------
// Saved Search API
// ---------------------------------------------------------------------------
import type {
  SavedSearchSummary,
  SavedSearchDetail,
  SaveSavedSearchRequest,
  GenerateListFromSearchRequest,
} from '../types/savedSearch'

export const savedSearchApi = {
  ...createCrudApi<SavedSearchSummary, SavedSearchDetail, SaveSavedSearchRequest>('/saved-searches'),

  getCount: (data: FilterEvaluationRequest) =>
    api.post<FilterCountResult>('/saved-searches/evaluate/count', data),

  getPreview: (data: FilterEvaluationRequest) =>
    api.post<ContactPreview[]>('/saved-searches/evaluate/preview', data),

  getFieldMetadata: () =>
    api.get<FilterFieldMetadata[]>('/saved-searches/field-metadata'),

  getTags: () =>
    api.get<TagOption[]>('/saved-searches/tags'),

  getContactsWithLocations: (id: string) =>
    api.get<ContactLocation[]>(`/saved-searches/${id}/contacts-with-locations`),

  generateList: (id: string, data: GenerateListFromSearchRequest) =>
    api.post<{ contactListId: string }>(`/saved-searches/${id}/generate-list`, data),
}

// ---------------------------------------------------------------------------
// Contact Detail API
// ---------------------------------------------------------------------------
import type { ContactDetail, UpdateContactRequest, ContactTag, CreateContactRequest, ContactMatchRequest, ContactMatchCandidate } from '../types/contactDetail'

export const contactDetailApi = {
  getDetail: (id: string) =>
    api.get<ContactDetail>(`/contacts/${id}/detail`),

  createContact: (data: CreateContactRequest) =>
    api.post<ContactDetail>('/contacts', data),

  updateContact: (id: string, data: UpdateContactRequest) =>
    api.put<ContactDetail>(`/contacts/${id}`, data),

  assignTag: (contactId: string, tagId: string) =>
    api.post<ContactTag>(`/contacts/${contactId}/tags`, { tagId }),

  removeTag: (contactId: string, tagId: string) =>
    api.delete(`/contacts/${contactId}/tags/${tagId}`),

  matchContact: (data: ContactMatchRequest) =>
    api.post<ContactMatchCandidate[]>('/contacts/match', data),
}

// ---------------------------------------------------------------------------
// Admin Users API
// ---------------------------------------------------------------------------
import type {
  AdminUser,
  UpdateUserRequest,
  CommitteeOption,
} from '../types/admin'

export const adminUserApi = {
  getAll: () =>
    api.get<AdminUser[]>('/admin/users'),

  getById: (id: string) =>
    api.get<AdminUser>(`/admin/users/${id}`),

  update: (id: string, data: UpdateUserRequest) =>
    api.put<AdminUser>(`/admin/users/${id}`, data),

  getCommitteeOptions: () =>
    api.get<CommitteeOption[]>('/admin/committees/options'),

  uploadPortrait: (id: string, file: File) =>
    uploadFile<{ portraitUrl: string }>(`/admin/users/${id}/portrait`, 'portrait', file),

  deletePortrait: (id: string) =>
    api.delete(`/admin/users/${id}/portrait`),
}

// ---------------------------------------------------------------------------
// Invites API (admin-issued account bootstrap, NEW-06)
// ---------------------------------------------------------------------------
import type { Invite, IssueInviteRequest, IssuedInvite } from '../types/invites'

export const invitesApi = {
  getAll: () =>
    api.get<Invite[]>('/admin/invites'),

  issue: (data: IssueInviteRequest) =>
    api.post<IssuedInvite>('/admin/invites', data),

  resend: (id: string) =>
    api.post<IssuedInvite>(`/admin/invites/${id}/resend`),

  revoke: (id: string) =>
    api.post<void>(`/admin/invites/${id}/revoke`),
}

// ---------------------------------------------------------------------------
// Admin Committees API
// ---------------------------------------------------------------------------
import type {
  AdminCommitteeSummary,
  AdminCommitteeDetail,
  SaveCommitteeRequest,
} from '../types/admin'

export const adminCommitteeApi =
  createCrudApi<AdminCommitteeSummary, AdminCommitteeDetail, SaveCommitteeRequest>('/admin/committees')

// ---------------------------------------------------------------------------
// Tag API
// ---------------------------------------------------------------------------
import type { TagSummaryDto, TagDetailDto, SaveTagRequest } from '../types/tag'

export const tagApi =
  createCrudApi<TagSummaryDto, TagDetailDto, SaveTagRequest>('/tags')

// ---------------------------------------------------------------------------
// Survey API
// ---------------------------------------------------------------------------
import type {
  SurveyGroupSummaryDto,
  SurveyGroupDetailDto,
  SaveSurveyGroupRequest,
  SurveyQuestionSummaryDto,
  SurveyQuestionDetailDto,
  SaveSurveyQuestionRequest,
  SurveyQuestionSearchRequest,
  PaginatedResponse as SurveyPaginatedResponse,
} from '../types/survey'

export const surveyApi = {
  // Groups
  ...(() => {
    const crud = createCrudApi<SurveyGroupSummaryDto, SurveyGroupDetailDto, SaveSurveyGroupRequest>('/surveys/groups')
    return {
      getAllGroups: crud.getAll,
      getGroupById: crud.getById,
      createGroup: crud.create,
      updateGroup: crud.update,
      deleteGroup: crud.delete,
    }
  })(),

  // Questions
  getAllQuestions: () =>
    api.get<SurveyQuestionSummaryDto[]>('/surveys/questions'),

  searchQuestions: (params: SurveyQuestionSearchRequest) =>
    api.get<SurveyPaginatedResponse<SurveyQuestionSummaryDto>>('/surveys/questions/search', { params }),

  getQuestionById: (id: string) =>
    api.get<SurveyQuestionDetailDto>(`/surveys/questions/${id}`),

  createQuestion: (data: SaveSurveyQuestionRequest) =>
    api.post<SurveyQuestionDetailDto>('/surveys/questions', data),

  updateQuestion: (id: string, data: SaveSurveyQuestionRequest) =>
    api.put<SurveyQuestionDetailDto>(`/surveys/questions/${id}`, data),

  deleteQuestion: (id: string) =>
    api.delete(`/surveys/questions/${id}`),
}

// ---------------------------------------------------------------------------
// Script API
// ---------------------------------------------------------------------------
import type {
  ScriptSummaryDto,
  ScriptDetailDto,
  SaveScriptRequest,
  DefaultResultSettingsDto,
  TagPickerDto,
  QuestionPickerDto,
  QuestionAnswerPickerDto,
  AttributePickerDto,
} from '../types/script'

export const scriptApi = {
  ...createCrudApi<ScriptSummaryDto, ScriptDetailDto, SaveScriptRequest>('/scripts'),

  getDefaultResultSettings: () =>
    api.get<DefaultResultSettingsDto[]>('/scripts/defaults/result-settings'),

  getTagPickerOptions: () =>
    api.get<TagPickerDto[]>('/scripts/picker/tags'),

  getQuestionPickerOptions: () =>
    api.get<QuestionPickerDto[]>('/scripts/picker/questions'),

  getQuestionAnswers: (questionId: string) =>
    api.get<QuestionAnswerPickerDto>(`/scripts/picker/questions/${questionId}/answers`),

  getAttributePicker: () =>
    api.get<AttributePickerDto[]>('/scripts/picker/attributes'),
}

// ---------------------------------------------------------------------------
// Campaign API
// ---------------------------------------------------------------------------
import type {
  CampaignSummaryDto,
  CampaignDetailDto,
  CampaignStatsDto,
  CampaignTurfRowDto,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  ItineraryWeekDto,
  ItineraryAssignmentDto,
  SaveItineraryWeekRequest,
  UpdateAssignmentStatusRequest,
  ToggleAvailabilityRequest,
  CampaignScriptConfigDto,
  SaveCampaignScriptConfigRequest,
  ScriptPickerDto,
  TurfMapPickerDto,
  ScheduleAnalysisRequest,
  ScheduleAnalysisDto,
  DailyReviewDto,
  PointOfContactDto,
  ImpactSettingsDto,
  ImpactContestDto,
} from '../types/campaign'
import type { ListRefreshStrategy } from '../types/contactList'
import type { EnrollmentCodeDto, SetCapRequest } from '../types/canvasserGrant'
import type { SignalCategory, SignalResolution, SignalQueuePage } from '../types/unionSignals'

export const campaignApi = {
  getAll: (params?: { status?: string; search?: string }) =>
    api.get<CampaignSummaryDto[]>('/campaigns', { params }),

  getById: (id: string) =>
    api.get<CampaignDetailDto>(`/campaigns/${id}`),

  getStats: (id: string) =>
    api.get<CampaignStatsDto>(`/campaigns/${id}/stats`),

  getTurfs: (id: string) =>
    api.get<CampaignTurfRowDto[]>(`/campaigns/${id}/turfs`),

  create: (data: CreateCampaignRequest) =>
    api.post<CampaignDetailDto>('/campaigns', data),

  update: (id: string, data: UpdateCampaignRequest) =>
    api.put<CampaignDetailDto>(`/campaigns/${id}`, data),

  delete: (id: string) =>
    api.delete(`/campaigns/${id}`),

  // Itinerary endpoints
  getItineraryWeek: (campaignId: string, weekStart: string) =>
    api.get<ItineraryWeekDto>(`/campaigns/${campaignId}/itinerary`, { params: withTzOffset({ weekStart }) }),

  saveItineraryWeek: (campaignId: string, data: SaveItineraryWeekRequest) =>
    api.put<ItineraryWeekDto>(`/campaigns/${campaignId}/itinerary`, data, { params: withTzOffset() }),

  updateAssignmentStatus: (campaignId: string, assignmentId: string, data: UpdateAssignmentStatusRequest) =>
    api.patch<ItineraryAssignmentDto>(`/campaigns/${campaignId}/itinerary/assignments/${assignmentId}/status`, data, { params: withTzOffset() }),

  toggleAvailability: (campaignId: string, data: ToggleAvailabilityRequest) =>
    api.put(`/campaigns/${campaignId}/itinerary/availability`, data),

  refreshLists: (campaignId: string) =>
    api.post(`/campaigns/${campaignId}/refresh`),

  // Campaign script config
  getScriptConfig: (campaignId: string) =>
    api.get<CampaignScriptConfigDto>(`/campaigns/${campaignId}/scripts`),

  saveScriptConfig: (campaignId: string, data: SaveCampaignScriptConfigRequest) =>
    api.put<CampaignScriptConfigDto>(`/campaigns/${campaignId}/scripts`, data),

  getScriptPicker: (campaignId: string) =>
    api.get<ScriptPickerDto[]>(`/campaigns/${campaignId}/scripts/picker/scripts`),

  getTagPicker: (campaignId: string) =>
    api.get<TagPickerDto[]>(`/campaigns/${campaignId}/scripts/picker/tags`),

  getTurfMapPicker: (campaignId: string) =>
    api.get<TurfMapPickerDto[]>(`/campaigns/${campaignId}/scripts/picker/turfmaps`),

  // Schedule analysis
  getScheduleAnalysis: (campaignId: string, data: ScheduleAnalysisRequest) =>
    api.post<ScheduleAnalysisDto>(`/campaigns/${campaignId}/itinerary/schedule-analysis`, data),

  // Daily review
  getDailyReview: (campaignId: string, date?: string) =>
    api.get<DailyReviewDto>(`/campaigns/${campaignId}/daily-review`, { params: withTzOffset({ date }) }),

  // Available canvassers for a date
  getAvailableCanvassers: (campaignId: string, date: string) =>
    api.get(`/campaigns/${campaignId}/available-canvassers`, { params: { date } }),

  // O2: union-signal queue (union tier only; 404 otherwise).
  signals: {
    list: (campaignId: string, category: SignalCategory, page: number, pageSize: number) =>
      api.get<SignalQueuePage>(`/campaigns/${campaignId}/signals`, { params: { category, page, pageSize } }),

    resolve: (campaignId: string, sessionId: string, action: SignalResolution) =>
      api.post(`/campaigns/${campaignId}/signals/${sessionId}/resolve`, { action }),
  },

  // L11 slice 4: shared campaign enrollment code (Task 9's frontend counterpart to the Task
  // 1-8 backend contract). get() is get-or-create -- the backend mints the code row on first
  // read, so there is no separate "create" call.
  enrollmentCode: {
    get: (campaignId: string) =>
      api.get<EnrollmentCodeDto>(`/campaigns/${campaignId}/enrollment-code`),

    setActive: (campaignId: string, on: boolean) =>
      api.post<EnrollmentCodeDto>(`/campaigns/${campaignId}/enrollment-code/${on ? 'on' : 'off'}`),

    rotate: (campaignId: string) =>
      api.post<EnrollmentCodeDto>(`/campaigns/${campaignId}/enrollment-code/rotate`),

    setCap: (campaignId: string, cap: number | null) =>
      api.put<EnrollmentCodeDto>(`/campaigns/${campaignId}/enrollment-code/cap`, { cap } satisfies SetCapRequest),
  },
}

// ---------------------------------------------------------------------------
// Campaign Canvasser-Grant API (L11 slice 1e, Task 13)
// ---------------------------------------------------------------------------
import type { CanvassThisRequest, CanvassThisResultDto } from '../types/grant'
import type {
  CanvasserGrantRow,
  MintCanvasserRequest,
  MintCanvasserResult,
  GrantSessionRow,
  RevokeCanvasserGrantRequest,
  CanvasserGrantFieldProfile,
} from '../types/canvasserGrant'

export const campaignCanvasserApi = {
  roster: (id: string) =>
    api.get<CanvasserGrantRow[]>(`/campaigns/${id}/canvasser-grants`),

  mint: (id: string, body: MintCanvasserRequest) =>
    api.post<MintCanvasserResult>(`/campaigns/${id}/canvasser-grants`, body),

  rotateCode: (id: string, grantId: string) =>
    api.post<{ shortCode: string; shortCodeExpiresAt: string }>(
      `/campaigns/${id}/canvasser-grants/${grantId}/rotate-code`,
    ),

  rotateLink: (id: string, grantId: string) =>
    api.post<{ magicLinkPath: string }>(
      `/campaigns/${id}/canvasser-grants/${grantId}/rotate-link`,
    ),

  sessions: (id: string, grantId: string) =>
    api.get<GrantSessionRow[]>(`/campaigns/${id}/canvasser-grants/${grantId}/sessions`),

  revokeSession: (id: string, grantId: string, sessionId: string) =>
    api.post<void>(`/campaigns/${id}/canvasser-grants/${grantId}/sessions/${sessionId}/revoke`),

  revokeGrant: (id: string, grantId: string, body: RevokeCanvasserGrantRequest) =>
    api.post<void>(`/campaigns/${id}/canvasser-grants/${grantId}/revoke`, body),

  // [L11-1e][LEGAL] Fix round 1: clobber-safety enforced BY SIGNATURE, not by comment/convention.
  // Deliberately NOT a separate PATCH -- reuses the campaign UPDATE endpoint (PUT
  // /api/campaigns/{id}) via campaignApi.update. CampaignService.UpdateAsync unconditionally
  // overwrites `description`/`refreshStrategyOverride` (no null-check, unlike
  // label/status/assignmentMode/defaultFieldProfile, which ARE guarded) -- a caller that only sent
  // `{ defaultFieldProfile }` (which type-checked fine against UpdateCampaignRequest, since
  // description/refreshStrategyOverride are BOTH optional there) would silently null out those two
  // fields server-side. Taking `current: CampaignDetailDto` instead of a hand-assembled
  // `UpdateCampaignRequest` makes that impossible: every other field is copied straight off the
  // campaign the caller already has in hand, so there is no code path that omits them.
  setFieldProfile: (id: string, current: CampaignDetailDto, profile: CanvasserGrantFieldProfile) =>
    campaignApi.update(id, {
      label: current.label,
      description: current.description ?? undefined,
      status: current.status,
      assignmentMode: current.assignmentMode,
      refreshStrategyOverride: (current.refreshStrategyOverride ?? undefined) as ListRefreshStrategy | undefined,
      defaultFieldProfile: profile,
    }),

  // [Phase 1] Its OWN partial endpoint — never the full-campaign update (see setFieldProfile).
  getPointOfContact: (id: string) =>
    api.get<PointOfContactDto>(`/campaigns/${id}/point-of-contact`),

  setPointOfContact: (id: string, body: PointOfContactDto) =>
    api.put<PointOfContactDto>(`/campaigns/${id}/point-of-contact`, body),

  // [Phase 8] Impact settings: their OWN partial endpoint (never the full-campaign update).
  getImpactSettings: (id: string) =>
    api.get<ImpactSettingsDto>(`/campaigns/${id}/impact-settings`),

  setImpactSettings: (id: string, body: ImpactSettingsDto) =>
    api.put<ImpactSettingsDto>(`/campaigns/${id}/impact-settings`, body),

  getImpactContests: (id: string) =>
    api.get<ImpactContestDto[]>(`/campaigns/${id}/impact-contests`),

  // [Canvass this] The organizer's own self-grant; the subject is always the caller (no id in the body).
  canvassThis: (campaignId: string, body: CanvassThisRequest) =>
    api.post<CanvassThisResultDto>(`/campaigns/${campaignId}/canvass-this`, body),
}

// ---------------------------------------------------------------------------
// Admin committee-wide Grant API (L11 slice 3b; LW-24 slice 3 merged canvasser+attendee)
// ---------------------------------------------------------------------------
import type { AdminGrantRow, AdminAttendeeGrantRow, BlockContactRequest } from '../types/adminGrant'

export const adminGrantApi = {
  // Committee-tree-wide grant lists. Each list is server-capped and answers a breach with a 400
  // whose `message` names the fix ("narrow with campaignId"/"...eventId") — callers surface it.
  listCanvassers: (campaignId?: string) =>
    api.get<AdminGrantRow[]>(
      campaignId ? `/admin/grants/canvassers?campaignId=${campaignId}` : '/admin/grants/canvassers',
    ),
  listAttendees: (eventId?: string) =>
    api.get<AdminAttendeeGrantRow[]>(
      eventId ? `/admin/grants/attendees?eventId=${eventId}` : '/admin/grants/attendees',
    ),

  // Person-level block/unblock — kind-agnostic (bars every grant the contact owns).
  block: (contactId: string, body: BlockContactRequest) =>
    api.post<void>(`/admin/grants/contacts/${contactId}/block`, body),
  unblock: (contactId: string) =>
    api.post<void>(`/admin/grants/contacts/${contactId}/unblock`),

  canvasserSessions: (grantId: string) =>
    api.get<GrantSessionRow[]>(`/admin/grants/canvassers/${grantId}/sessions`),
  attendeeSessions: (grantId: string) =>
    api.get<GrantSessionRow[]>(`/admin/grants/attendees/${grantId}/sessions`),

  revokeCanvasserGrant: (grantId: string, body: RevokeCanvasserGrantRequest) =>
    api.post<void>(`/admin/grants/canvassers/${grantId}/revoke`, body),
  revokeAttendeeGrant: (grantId: string, body: RevokeCanvasserGrantRequest) =>
    api.post<void>(`/admin/grants/attendees/${grantId}/revoke`, body),

  revokeCanvasserSession: (grantId: string, sessionId: string) =>
    api.post<void>(`/admin/grants/canvassers/${grantId}/sessions/${sessionId}/revoke`),
  revokeAttendeeSession: (grantId: string, sessionId: string) =>
    api.post<void>(`/admin/grants/attendees/${grantId}/sessions/${sessionId}/revoke`),
}

// ---------------------------------------------------------------------------
// Turf API
// ---------------------------------------------------------------------------
import type {
  TurfDto,
  TurfDetailDto,
  CreateTurfRequest,
  UpdateTurfRequest,
  CutTurfRequest,
  ExportTurfToListRequest,
  TurfFilters,
  CandidateSearchRequest,
  CandidateSearchResponse,
  AddOpportunisticStopRequest,
} from '../types/turf'

export const turfApi = {
  getAll: (params?: TurfFilters) =>
    api.get<TurfDto[]>('/turfs', { params }),

  getById: (id: string, scheduleAt?: string) =>
    api.get<TurfDetailDto>(`/turfs/${id}`, { params: scheduleAt ? { scheduleAt } : undefined }),

  create: (data: CreateTurfRequest) =>
    api.post<TurfDetailDto>('/turfs', data),

  update: (id: string, data: UpdateTurfRequest) =>
    api.put<TurfDetailDto>(`/turfs/${id}`, data),

  delete: (id: string) =>
    api.delete(`/turfs/${id}`),

  cut: (data: CutTurfRequest) =>
    api.post<TurfDetailDto>('/turfs/cut', data),

  optimizeOrder: (id: string) =>
    api.post<{ message: string }>(`/turfs/${id}/optimize-order`),

  exportToList: (id: string, data: ExportTurfToListRequest) =>
    api.post<{ listId: string }>(`/turfs/${id}/export-to-list`, data),

  setPlacements: (id: string, items: { turfMemberId: string; locationSource: 'Home' | 'Work' }[]) =>
    api.put<void>(`/turfs/${id}/placements`, { items }),

  moveMember: (id: string, memberId: string, toPosition: number) =>
    api.post<void>(`/turfs/${id}/members/${memberId}/move`, { toPosition }),

  removeMember: (id: string, memberId: string) =>
    api.delete<void>(`/turfs/${id}/members/${memberId}`),

  // [O4] Opportunistic stops (union tier, campaign turfs)
  findCandidates: (id: string, body: CandidateSearchRequest) =>
    api.post<CandidateSearchResponse>(`/turfs/${id}/opportunistic-candidates`, body),

  addOpportunisticStop: (id: string, body: AddOpportunisticStopRequest) =>
    api.post<{ id: string }>(`/turfs/${id}/opportunistic-stops`, body),

  updateStopScript: (id: string, stopId: string, scriptId: string | null) =>
    api.put<void>(`/turfs/${id}/opportunistic-stops/${stopId}`, { scriptId }),

  removeOpportunisticStop: (id: string, stopId: string) =>
    api.delete<void>(`/turfs/${id}/opportunistic-stops/${stopId}`),
}

// ---------------------------------------------------------------------------
// Turf Map API
// ---------------------------------------------------------------------------
import type {
  TurfMapSummaryDto,
  TurfMapDetailDto,
  BatchCutTurfsRequest,
  UpdateTurfMapRequest,
  TurfMapFilters,
  HouseholdDto,
  AutoGenerateTurfMapRequest,
  MergeListResponse,
} from '../types/turf'
import type { JobEnqueuedDto } from '../types/job'
import type {
  RecutPreviewRequest,
  AcceptRecutRequest,
  ManualRecutRequest,
  ForwardAssignmentsRequest,
  ActiveCanvassingDto,
} from '../types/campaign'

export const turfMapApi = {
  getAll: (params?: TurfMapFilters) =>
    api.get<TurfMapSummaryDto[]>('/turf-maps', { params }),

  getById: (id: string) =>
    api.get<TurfMapDetailDto>(`/turf-maps/${id}`),

  batchCut: (data: BatchCutTurfsRequest) =>
    api.post<TurfMapDetailDto>('/turf-maps/batch-cut', data),

  update: (id: string, data: UpdateTurfMapRequest) =>
    api.put<TurfMapDetailDto>(`/turf-maps/${id}`, data),

  delete: (id: string) =>
    api.delete(`/turf-maps/${id}`),

  getHouseholds: (id: string, fromSourceList?: boolean) =>
    api.get<HouseholdDto[]>(`/turf-maps/${id}/households`, {
      params: fromSourceList ? { fromSourceList: true } : undefined,
    }),

  autoGenerate: (data: AutoGenerateTurfMapRequest) =>
    api.post<JobEnqueuedDto>('/turf-maps/auto-generate', data),

  autoGeneratePreview: (data: AutoGenerateTurfMapRequest) =>
    api.post<JobEnqueuedDto>('/turf-maps/auto-generate-preview', data),

  recutPreview: (turfMapId: string, data: RecutPreviewRequest) =>
    api.post<JobEnqueuedDto>(`/turf-maps/${turfMapId}/recut-preview`, data),

  recutAccept: (turfMapId: string, data: AcceptRecutRequest) =>
    api.post<TurfMapDetailDto>(`/turf-maps/${turfMapId}/recut-accept`, data),

  manualRecut: (turfMapId: string, data: ManualRecutRequest) =>
    api.post<TurfMapDetailDto>(`/turf-maps/${turfMapId}/manual-recut`, data),

  forwardAssignments: (turfMapId: string, data: ForwardAssignmentsRequest) =>
    api.post(`/turf-maps/${turfMapId}/forward-assignments`, data),

  getContactsWithEmployment: (turfMapId: string) =>
    api.get<ContactWithEmployment[]>(`/turf-maps/${turfMapId}/contacts-with-employment`),

  mergeList: (turfMapId: string, contactListId: string) =>
    api.post<MergeListResponse>(`/turf-maps/${turfMapId}/merge-list`, { contactListId }),

  // [Canvass this, CT-16] tzOffset is always sent (the server treats a missing one as 0 = UTC).
  activeCanvassing: (turfMapId: string, targetDate: string, tzOffset: number = new Date().getTimezoneOffset()) =>
    api.get<ActiveCanvassingDto>(`/turf-maps/${turfMapId}/active-canvassing`, {
      params: { targetDate, tzOffset },
    }),
}

// ---------------------------------------------------------------------------
// Profile API
// ---------------------------------------------------------------------------
import type { ProfileDto, UpdateProfileRequest } from '../types/profile'
import type { InvitePreview } from '../types/invites'

// Minimal shape of the login/accept-invite response, consistent with
// useAuth's AuthUser (id/email/role only — no token, it's an HttpOnly cookie).
interface AcceptInviteResponse {
  user: {
    id: string
    email: string
    role: string
  }
  /** [Native B1, NB1-2] Native-origin requests only: the `od_` device session token. */
  sessionToken?: string | null
  sessionExpiresAt?: string | null
}

export const authApi = {
  logout: () => api.post('/auth/logout'),

  invitePreview: (token: string) =>
    api.get<InvitePreview>(`/auth/invite/${token}`),

  acceptInvite: (data: { token: string; password: string; firstName: string; lastName: string; deviceLabel?: string }) =>
    api.post<AcceptInviteResponse>('/auth/accept-invite', data),
}

import type { TodayAssignmentDto } from '../types/canvasser'
import type { GrantIdentity, GrantRedeemResult, RedeemGrantRequest, RedeemGrantByCodeRequest, EnrollRequest, EnrollResult, GrantProfile, UpdateGrantProfileRequest, GrantImpact } from '../types/grant'

export const grantApi = {
  redeem: (body: RedeemGrantRequest) => api.post<GrantRedeemResult>('/grant/redeem', body),
  // [CANV-7/8] Redeem a personal organizer-minted short code. Resolves an already-minted grant
  // (no name capture); a 404 means the code is not a personal short code, so JoinPage falls
  // through to the shared-campaign enroll flow.
  redeemCode: (body: RedeemGrantByCodeRequest) => api.post<GrantRedeemResult>('/grant/redeem-code', body),
  // L11 slice 4: anonymous self-enrollment against a shared campaign code. Mints a Contact +
  // Canvasser grant + session on success. See JoinPage for the two-step terms handshake.
  enroll: (body: EnrollRequest) => api.post<EnrollResult>('/grant/enroll', body),
  // [Phase 1] tzOffset only decides "next shift after today" on the campaign home.
  me: () => api.get<GrantIdentity>('/grant/me', { params: withTzOffset() }),
  // [CF-1 P3] Today's assignments (canvass turfs, action/chase routes, call batches).
  getToday: (tzOffset?: number) =>
    api.get<TodayAssignmentDto[]>('/grant/today', { params: withTzOffset({}, tzOffset) }),
  logout: () => api.post('/grant/logout'),
  // [LEGAL] The device confirms it wiped its cached PII. Anonymous + best-effort: the caller fires
  // it after a successful wipeOfflineData() and ignores the result (the session may already be dead).
  wipeAck: () => api.post('/grant/wipe-ack'),
  devSession: (body: { grantId: string; deviceLabel?: string }) =>
    api.post<GrantIdentity>('/grant/dev/session', body),
  // [Phase 7] The canvasser's OWN profile + photo. Online-only.
  profile: () => api.get<GrantProfile>('/grant/me/profile', { params: withTzOffset() }),
  updateProfile: (body: UpdateGrantProfileRequest) => api.patch('/grant/me/profile', body),
  uploadPortrait: (image: Blob) => {
    const form = new FormData()
    form.append('portrait', image, 'portrait.jpg')
    return api.post<{ portraitUrl: string }>('/grant/me/portrait', form, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0,
    })
  },
  deletePortrait: () => api.delete('/grant/me/portrait'),
  // [Phase 8] The canvasser's impact card. Online-only.
  impact: () => api.get<GrantImpact>('/grant/me/impact'),
}

export const profileApi = {
  get: () =>
    api.get<ProfileDto>('/auth/profile'),

  update: (data: UpdateProfileRequest) =>
    api.put<ProfileDto>('/auth/profile', data),

  uploadPortrait: (file: File) =>
    uploadFile<{ portraitUrl: string }>('/auth/profile/portrait', 'portrait', file),

  deletePortrait: () =>
    api.delete('/auth/profile/portrait'),
}

// ---------------------------------------------------------------------------
// Worksite API (individual locations where people work)
// ---------------------------------------------------------------------------
import type {
  PaginatedWorksiteResult,
  WorksiteDetail,
  SaveWorksiteRequest,
  WorksiteSearchParams,
  WorksiteLocation,
  BusinessSummary,
  SaveBusinessRequest,
  EmploymentImportResult,
  UnionCoverageOverride,
} from '../types/business'
import type { UpdateNotesRequest } from '../types/union'

import type { BusinessSuggest, WorksiteSuggest, MergeRequest, MergeResult } from '../types/business'
import type {
  ArrivalInstructionView, SaveArrivalInstructionRequest,
  CopyArrivalInstructionRequest, CopyArrivalInstructionResult,
} from '../types/business'

export const worksiteApi = {
  suggest: (query: string, businessId?: string, limit?: number) =>
    api.get<WorksiteSuggest[]>('/worksites/suggest', { params: { query, businessId, limit } }),

  merge: (data: MergeRequest) =>
    api.post<MergeResult>('/worksites/merge', data),

  search: (params?: WorksiteSearchParams) =>
    api.get<PaginatedWorksiteResult>('/worksites', { params }),

  getById: (id: string) =>
    api.get<WorksiteDetail>(`/worksites/${id}`),

  create: (data: SaveWorksiteRequest) =>
    api.post<WorksiteDetail>('/worksites', data),

  update: (id: string, data: SaveWorksiteRequest) =>
    api.put<WorksiteDetail>(`/worksites/${id}`, data),

  delete: (id: string) =>
    api.delete(`/worksites/${id}`),

  linkToBusiness: (worksiteId: string, businessId: string, unionOverrides?: UnionCoverageOverride[]) =>
    api.patch(`/worksites/${worksiteId}/link-business`, { businessId, unionOverrides }),

  updateNotes: (id: string, data: UpdateNotesRequest) =>
    api.patch(`/worksites/${id}/notes`, data),

  getLocations: () =>
    api.get<WorksiteLocation[]>('/worksites/locations'),

  getArrival: (id: string) =>
    api.get<ArrivalInstructionView>(`/worksites/${id}/arrival-instruction`),

  saveArrival: (id: string, data: SaveArrivalInstructionRequest) =>
    api.put<ArrivalInstructionView>(`/worksites/${id}/arrival-instruction`, data),

  copyArrival: (id: string, data: CopyArrivalInstructionRequest) =>
    api.post<CopyArrivalInstructionResult>(`/worksites/${id}/arrival-instruction/copy`, data),

  importCsv: (file: File, options?: {
    updateExisting?: boolean
    createMissingWorksites?: boolean
    createMissingUnions?: boolean
    defaultWorksiteType?: string
    maxRecords?: number
  }) => {
    const formData = new FormData()
    formData.append('file', file)
    const params = new URLSearchParams()
    if (options?.updateExisting !== undefined) params.set('updateExisting', String(options.updateExisting))
    if (options?.createMissingWorksites !== undefined) params.set('createMissingWorksites', String(options.createMissingWorksites))
    if (options?.createMissingUnions !== undefined) params.set('createMissingUnions', String(options.createMissingUnions))
    if (options?.defaultWorksiteType) params.set('defaultWorksiteType', options.defaultWorksiteType)
    if (options?.maxRecords) params.set('maxRecords', String(options.maxRecords))
    return api.post<EmploymentImportResult>(`/worksites/import?${params.toString()}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

// ---------------------------------------------------------------------------
// Business API (parent grouping entity, e.g., "Kroger")
// ---------------------------------------------------------------------------
import type {
  BusinessSearchParams, PaginatedBusinessResult, BusinessDetail,
  PaginatedBusinessEmployeesResult,
} from '../types/business'

export const businessApi = {
  getAll: () =>
    api.get<BusinessSummary[]>('/businesses'),

  suggest: (query: string, limit?: number) =>
    api.get<BusinessSuggest[]>('/businesses/suggest', { params: { query, limit } }),

  search: (params?: BusinessSearchParams) =>
    api.get<PaginatedBusinessResult>('/businesses/search', { params }),

  getDetail: (id: string) =>
    api.get<BusinessDetail>(`/businesses/${id}/detail`),

  getEmployees: (id: string, params?: { page?: number; pageSize?: number; search?: string }) =>
    api.get<PaginatedBusinessEmployeesResult>(`/businesses/${id}/employees`, { params }),

  create: (data: SaveBusinessRequest) =>
    api.post<BusinessSummary>('/businesses', data),

  update: (id: string, data: SaveBusinessRequest) =>
    api.put<BusinessSummary>(`/businesses/${id}`, data),

  delete: (id: string) =>
    api.delete(`/businesses/${id}`),

  updateNotes: (id: string, data: UpdateNotesRequest) =>
    api.patch(`/businesses/${id}/notes`, data),

  merge: (data: MergeRequest) =>
    api.post<MergeResult>('/businesses/merge', data),

  getArrival: (id: string) =>
    api.get<ArrivalInstructionView>(`/businesses/${id}/arrival-instruction`),

  saveArrival: (id: string, data: SaveArrivalInstructionRequest) =>
    api.put<ArrivalInstructionView>(`/businesses/${id}/arrival-instruction`, data),

  copyArrival: (id: string, data: CopyArrivalInstructionRequest) =>
    api.post<CopyArrivalInstructionResult>(`/businesses/${id}/arrival-instruction/copy`, data),
}

// ---------------------------------------------------------------------------
// Union API
// ---------------------------------------------------------------------------
import type {
  PaginatedUnionResult,
  UnionDetail,
  SaveUnionRequest,
  UnionSearchParams,
  UnionWorksiteAffiliation,
  UnionBusinessAffiliation,
  AffiliateWorksiteRequest,
  AffiliateBusinessRequest,
  UpdateBusinessAffiliationRequest,
  UpdateWorksiteAffiliationRequest,
  UpdateWorksiteOverrideRequest,
  WorksiteOverrideRequest,
  AddStewardRequest,
  Steward,
  UnionMembersParams,
  PaginatedUnionMembersResult,
} from '../types/union'

export const unionApi = {
  search: (params?: UnionSearchParams) =>
    api.get<PaginatedUnionResult>('/unions', { params }),

  getById: (id: string) =>
    api.get<UnionDetail>(`/unions/${id}`),

  getMembers: (id: string, params?: UnionMembersParams) =>
    api.get<PaginatedUnionMembersResult>(`/unions/${id}/members`, { params }),

  create: (data: SaveUnionRequest) =>
    api.post<UnionDetail>('/unions', data),

  update: (id: string, data: SaveUnionRequest) =>
    api.put<UnionDetail>(`/unions/${id}`, data),

  delete: (id: string) =>
    api.delete(`/unions/${id}`),

  // Worksite-level affiliations
  affiliateWorksite: (unionId: string, data: AffiliateWorksiteRequest) =>
    api.post<UnionWorksiteAffiliation>(`/unions/${unionId}/affiliations`, data),

  updateWorksiteAffiliation: (unionId: string, worksiteId: string, data: UpdateWorksiteAffiliationRequest) =>
    api.put<UnionWorksiteAffiliation>(`/unions/${unionId}/affiliations/${worksiteId}`, data),

  unaffiliateWorksite: (unionId: string, worksiteId: string) =>
    api.delete(`/unions/${unionId}/affiliations/${worksiteId}`),

  // Business-level affiliations
  affiliateBusiness: (unionId: string, data: AffiliateBusinessRequest) =>
    api.post<UnionBusinessAffiliation>(`/unions/${unionId}/affiliations/business`, data),

  updateBusinessAffiliation: (unionId: string, businessId: string, data: UpdateBusinessAffiliationRequest) =>
    api.put<UnionBusinessAffiliation>(`/unions/${unionId}/affiliations/business/${businessId}`, data),

  unaffiliateBusiness: (unionId: string, businessId: string) =>
    api.delete(`/unions/${unionId}/affiliations/business/${businessId}`),

  // Worksite overrides (under a business contract)
  createWorksiteOverride: (unionId: string, businessId: string, data: WorksiteOverrideRequest) =>
    api.post<UnionWorksiteAffiliation>(`/unions/${unionId}/affiliations/business/${businessId}/overrides`, data),

  updateWorksiteOverride: (unionId: string, businessId: string, worksiteId: string, data: UpdateWorksiteOverrideRequest) =>
    api.put<UnionWorksiteAffiliation>(`/unions/${unionId}/affiliations/business/${businessId}/overrides/${worksiteId}`, data),

  removeWorksiteOverride: (unionId: string, businessId: string, worksiteId: string) =>
    api.delete(`/unions/${unionId}/affiliations/business/${businessId}/overrides/${worksiteId}`),

  // Steward management
  addSteward: (unionId: string, worksiteId: string, data: AddStewardRequest) =>
    api.post<Steward>(`/unions/${unionId}/affiliations/worksites/${worksiteId}/stewards`, data),

  removeSteward: (unionId: string, worksiteId: string, stewardId: string) =>
    api.delete(`/unions/${unionId}/affiliations/worksites/${worksiteId}/stewards/${stewardId}`),
}

// ---------------------------------------------------------------------------
// Canvasser API
// ---------------------------------------------------------------------------
import type {
  MyTurfAssignmentDto,
  ResolvedScriptDto,
  RecordVisitRequest,
  RecordVisitResponse,
  BatchRecordVisitsRequest,
  BatchRecordVisitsResponse,
  CampaignTagDto,
  AddCanvasserNoteRequest,
  CanvasserNoteDto,
  ComputeRouteRequest,
  ComputeRouteResponse,
  AddToTurfRequest,
  AddToTurfResponse,
  HouseholdLookupContactDto,
  CreateDoorContactRequest,
  DoorContactCreatedDto,
  StopLocationChangedDto,
  RouteOutcomeRequest,
  RouteOutcomeResult,
} from '../types/canvasser'

export const canvasserApi = {
  // [Phase 1] The server derives the campaign from the grant (spec §6.1); no client campaignId.
  getMyTurfs: (tzOffset?: number) =>
    api.get<MyTurfAssignmentDto[]>('/canvassing/my-turfs', {
      params: withTzOffset({}, tzOffset),
    }),

  // CF-1 P4: grant-facing call-list reads/writes (the canvasser hooks/screens land in Task 9).
  getCallBatch: (batchId: string, tzOffset?: number) =>
    api.get<FollowUpCallBatchDetailDto>(`/grant/follow-up/call-batches/${batchId}`, {
      params: withTzOffset({}, tzOffset),
    }),
  // 204 = no script resolved for this item; mapped to null.
  getCallItemScript: (batchId: string, itemId: string) =>
    api.get<ResolvedScriptDto>(`/grant/follow-up/call-batches/${batchId}/items/${itemId}/script`)
      .then(r => (r.status === 204 || !r.data ? null : r.data)),
  recordCalls: (calls: RecordCallSubmission[], tzOffset?: number) =>
    api.post<{ results: RecordCallResult[] }>('/grant/follow-up/calls', { calls }, {
      params: withTzOffset({}, tzOffset),
    }),

  resolveScript: (contactId: string, turfId: string) =>
    api.get<ResolvedScriptDto>('/canvassing/resolve-script', {
      params: withTzOffset({ contactId, turfId }),
    }),

  // [CANV-6] Grant-authorized contact detail for the canvasser session screen. This MUST stay under
  // /canvassing (not the organizer /contacts/{id}/detail): the organizer endpoint is JWT-only, so a
  // grant session 401s there, and the interceptor's URL classifier would route that 401 to the
  // organizer /login instead of the canvasser redeem screen. The backend returns a MINIMAL projection
  // (a subset of ContactDetail — no employments/analytics/impact/list-membership/Catalist); typed as
  // ContactDetail only because every field the canvasser UI reads is present in that subset. Do NOT
  // rely on the omitted organizer fields on this path — they are undefined at runtime.
  getContactDetail: (contactId: string) =>
    api.get<ContactDetail>(`/canvassing/contacts/${contactId}/detail`, {
      params: withTzOffset(),
    }),

  recordVisit: (data: RecordVisitRequest) =>
    api.post<RecordVisitResponse>('/canvassing/record-visit', data, {
      params: withTzOffset(),
    }),

  batchRecordVisits: (data: BatchRecordVisitsRequest) =>
    api.post<BatchRecordVisitsResponse>('/canvassing/batch-record-visits', data, {
      params: withTzOffset(),
    }),

  // [CF-1 P3] Action-route outcomes ("Dropped off" / "Couldn't"). An ALL-rejected batch answers
  // 400 {message, results}; callers read the per-item results off the error response.
  recordRouteOutcomes: (outcomes: RouteOutcomeRequest[], tzOffset?: number) =>
    api.post<{ results: RouteOutcomeResult[] }>('/grant/follow-up/route-outcomes', { outcomes }, {
      params: withTzOffset({}, tzOffset),
    }),

  getCampaignTags: () =>
    api.get<CampaignTagDto[]>('/canvassing/campaign-tags', {
      params: withTzOffset(),
    }),

  addNote: (contactId: string, data: AddCanvasserNoteRequest) =>
    api.post<CanvasserNoteDto>(`/canvassing/contacts/${contactId}/notes`, data, {
      params: withTzOffset(),
    }),

  assignTag: (contactId: string, tagId: string) =>
    api.post(`/canvassing/contacts/${contactId}/tags/${tagId}`, null, {
      params: withTzOffset(),
    }),

  removeTag: (contactId: string, tagId: string) =>
    api.delete(`/canvassing/contacts/${contactId}/tags/${tagId}`, {
      params: withTzOffset(),
    }),

  setDoNotContact: (contactId: string) =>
    api.patch(`/canvassing/contacts/${contactId}/dnc`, null, {
      params: withTzOffset(),
    }),

  computeRoute: (data: ComputeRouteRequest) =>
    api.post<ComputeRouteResponse>('/canvassing/compute-route', data),

  addToTurf: (data: AddToTurfRequest) =>
    api.post<AddToTurfResponse>('/canvassing/add-to-turf', data, {
      params: withTzOffset(),
    }),

  // [Phase 3 T13 / Phase 4 D5] Name lookup anchored on a stop already on the caller's turf.
  householdLookup: (turfId: string, turfMemberId: string, q?: string) =>
    api.get<HouseholdLookupContactDto[]>(`/canvassing/turfs/${turfId}/household-lookup`, {
      params: withTzOffset({ turfMemberId, ...(q ? { q } : {}) }),
    }),

  // [Phase 3 P3-2] Creates the person at the anchor stop; the caller then calls addToTurf.
  createDoorContact: (turfId: string, body: CreateDoorContactRequest) =>
    api.post<DoorContactCreatedDto>(`/canvassing/turfs/${turfId}/door-contacts`, body, {
      params: withTzOffset(),
    }),

  // [Phase 3 B2 / Phase 4 D4] "Visit at home today": flips this member's placement Work -> Home.
  visitAtHome: (turfMemberId: string) =>
    api.post<StopLocationChangedDto>(`/canvassing/turf-members/${turfMemberId}/visit-at-home`, null, {
      params: withTzOffset(),
    }),
}

// ---------------------------------------------------------------------------
// Analytics API
// ---------------------------------------------------------------------------
import type {
  AnalyticsContactsRequest,
  AnalyticsDashboardRequest,
  AnalyticsHighValueRequest,
  MultiProfileHighValueRequest,
  AnalyticsExportRequest,
  AnalyticsCountScopeRequest,
  AnalyticsApplyTagsRequest,
  AnalyticsCreateTurfRequest,
  AnalyticsCreateTurfResponse,
  EnrichedContactLocation,
  DashboardData,
  HighValueVoter,
  ListFiltersResponse,
  ScoringProfile,
} from '../types/analyticsMap'

export const analyticsApi = {
  getContacts: (data: AnalyticsContactsRequest) =>
    api.post<EnrichedContactLocation[]>('/analytics/contacts', data),

  getDashboard: (data: AnalyticsDashboardRequest) =>
    api.post<DashboardData>('/analytics/dashboard', data),

  getHighValueVoters: (data: AnalyticsHighValueRequest) =>
    api.post<HighValueVoter[]>('/analytics/high-value', data),

  exportToList: (data: AnalyticsExportRequest) =>
    api.post<{ listId: string }>('/analytics/export-to-list', data),

  getListFilters: (listId: string) =>
    api.get<ListFiltersResponse>(`/analytics/list-filters/${listId}`),

  getHighValueMulti: (data: MultiProfileHighValueRequest) =>
    api.post<HighValueVoter[]>('/analytics/high-value-multi', data),

  getScoringProfiles: () =>
    api.get<ScoringProfile[]>('/analytics/scoring-profiles'),

  countScope: (data: AnalyticsCountScopeRequest) =>
    api.post<{ count: number }>('/analytics/count-scope', data),

  applyTags: (data: AnalyticsApplyTagsRequest) =>
    api.post<{ appliedCount: number }>('/analytics/apply-tags', data),

  createTurf: (data: AnalyticsCreateTurfRequest) =>
    api.post<AnalyticsCreateTurfResponse>('/analytics/create-turf', data),
}

// ---------------------------------------------------------------------------
// Geo Analytics API
// ---------------------------------------------------------------------------
import type * as GeoJSON from 'geojson'
import type {
  GeoLiveMetricsRequest,
  GeoMetricRow,
  GeoMetricsRequest,
  GeoUnitFullProfile,
  GeoUnitProfile,
  GeoUnitsParams,
  GeoUnitTypeKey,
  UnionFootprintDetail,
} from '../types/geoAnalytics'
import type { GeoDistrict } from '../components/lobbyDay/districtCatalog'

export const geoApi = {
  getDistricts: (type: string, state: string) =>
    api.get<GeoDistrict[]>('/geo/districts', { params: { type, state } }),

  getUnits: (params: GeoUnitsParams) =>
    api.get<GeoJSON.FeatureCollection>('/geo/units', { params }),
  getUnitsVersion: (params: { type: string; state: string }) =>
    api.get<{ version: string }>('/geo/units/version', { params }),
  getMetrics: (data: GeoMetricsRequest) =>
    api.post<GeoMetricRow[]>('/geo/metrics', data),
  getUnitProfile: (id: string) =>
    api.get<GeoUnitProfile>(`/geo/units/${id}/profile`),
  getLiveMetrics: (data: GeoLiveMetricsRequest) =>
    api.post<GeoMetricRow[]>('/geo/live-metrics', data),
  getMetricKeys: (params: { type: GeoUnitTypeKey; state: string }) =>
    api.get<string[]>('/geo/metric-keys', { params }),
  getFullProfile: (id: string) =>
    api.get<GeoUnitFullProfile>(`/geo/units/${id}/full-profile`),
  getUnionFootprintDetail: (id: string) =>
    api.get<UnionFootprintDetail>(`/geo/units/${id}/union-footprint`),
  getDistrictBounds: (state: string, type: string, code: string) =>
    api.get<number[]>('/geo/district-bounds', { params: { state, type, code } }),
}

// ---------------------------------------------------------------------------
// Election Simulator API
// ---------------------------------------------------------------------------
import type {
  ContestRequest,
  ScenarioDetail,
  ScenarioSave,
  ScenarioSummary,
  SimBaselineResponse,
  SimRunRequest,
  SimRunResponse,
  SimTargetingCount,
  SimTargetingCountRequest,
} from '../types/electionSim'

export const simApi = {
  baseline: (data: ContestRequest) => api.post<SimBaselineResponse>('/sim/baseline', data),
  run: (data: SimRunRequest) => api.post<SimRunResponse>('/sim/run', data),
  targetingCount: (data: SimTargetingCountRequest) =>
    api.post<SimTargetingCount>('/sim/targeting/count', data),
  listScenarios: () => api.get<ScenarioSummary[]>('/sim/scenarios'),
  getScenario: (id: string) => api.get<ScenarioDetail>(`/sim/scenarios/${id}`),
  createScenario: (data: ScenarioSave) => api.post<ScenarioDetail>('/sim/scenarios', data),
  updateScenario: (id: string, data: ScenarioSave) => api.put<ScenarioDetail>(`/sim/scenarios/${id}`, data),
  deleteScenario: (id: string) => api.delete(`/sim/scenarios/${id}`),
  pinScenario: (id: string, geoUnitId: string | null) => api.post(`/sim/scenarios/${id}/pin`, { geoUnitId }),
  getPinnedScenario: (geoUnitId: string) => api.get<ScenarioSummary | null>(`/sim/scenarios/pinned/${geoUnitId}`),
}

// ---------------------------------------------------------------------------
// Demo API
// ---------------------------------------------------------------------------
import type { SimulateCampaignRequest, SimulationResetResult } from '../types/demo'

export const demoApi = {
  simulate: (campaignId: string, req: SimulateCampaignRequest) =>
    api.post<JobEnqueuedDto>(`/demo/campaigns/${campaignId}/simulate`, req),
  reset: (campaignId: string) =>
    api.post<SimulationResetResult>(`/demo/campaigns/${campaignId}/simulate/reset`),
}

// ---------------------------------------------------------------------------
// Employment API
// ---------------------------------------------------------------------------
import type { Employment, SaveEmploymentRequest, BestLocationResult, LocationTypesRequest } from '../types/employment'

export const employmentApi = {
  getByContact: (contactId: string) =>
    api.get<Employment[]>(`/contacts/${contactId}/employment`),

  getById: (contactId: string, id: string) =>
    api.get<Employment>(`/contacts/${contactId}/employment/${id}`),

  create: (contactId: string, data: SaveEmploymentRequest) =>
    api.post<Employment>(`/contacts/${contactId}/employment`, data),

  update: (contactId: string, id: string, data: SaveEmploymentRequest) =>
    api.put<Employment>(`/contacts/${contactId}/employment/${id}`, data),

  delete: (contactId: string, id: string) =>
    api.delete(`/contacts/${contactId}/employment/${id}`),

  getBestLocation: (contactId: string, dateTime?: string) =>
    api.get<BestLocationResult>(`/contacts/${contactId}/best-location`, {
      params: dateTime ? { dateTime } : undefined,
    }),

  getLocationTypes: (data: LocationTypesRequest) =>
    api.post<Record<string, string>>('/contacts/location-types', data),
}

// ---------------------------------------------------------------------------
// Lobby Day API
// ---------------------------------------------------------------------------
import type {
  LobbyDayEventSummaryDto, LobbyDayEventDetailDto, SaveLobbyDayEventRequest,
  LobbyDayAskDto, SaveLobbyDayAskRequest, LobbyDayAttendeeDto,
  LegislatorDto, SaveLegislatorRequest, ImportLegislatorsResult, SyncLegislatorsRequest,
  LobbyScheduleDto, SaveLobbyScheduleRequest, LobbyPacketDto, AttendeeScheduleDto,
  LegislatorOutreachRowDto, SaveOutreachRequest, EventDebriefDto, RecordOutcomeRequest,
  AttachMeetingAskRequest,
  ReconciliationDto, SaveVotesRequest, SaveVotesResponse, ApplyReconciliationResponse,
  CreateFollowUpListRequest, CreateFollowUpListResponse,
  TargetCoverageDto, AddTargetRequest, UpdateTargetRequest, PriorEventDto,
  SetTargetAllRequest, SetMemberTargetRequest, ConstituentCountRow,
  BillSearchCandidate, LinkBillRequest, AskBill, BillSession,
  AttendeeDeliveryMode, EventPrintPacketsDto,
  LogisticsDto, SaveLogisticsRequest,
  AskResponse,
  LegislatorNoteDto, CreateLegislatorNoteRequest,
  AttendanceStatus,
  AttendeeProfileDto, UpdateAttendeeProfileRequest,
  AskTemplateDto, SaveAskTemplateRequest, RenameAskTemplateRequest,
  AddAttendeesResult,
  LinkResendAccepted,
  LobbyAttachmentList, LobbyAttachment,
  MeetingRole, MeetingStaffDto, DayOfLeadDto,
} from '../types/lobbyDay'

export const lobbyDayApi = {
  getEvents: (params?: { status?: string }) =>
    api.get<LobbyDayEventSummaryDto[]>('/lobby-day/events', { params }),

  getEvent: (id: string) =>
    api.get<LobbyDayEventDetailDto>(`/lobby-day/events/${id}`),

  createEvent: (data: SaveLobbyDayEventRequest) =>
    api.post<LobbyDayEventDetailDto>('/lobby-day/events', data),

  updateEvent: (id: string, data: SaveLobbyDayEventRequest) =>
    api.put<LobbyDayEventDetailDto>(`/lobby-day/events/${id}`, data),

  deleteEvent: (id: string) =>
    api.delete(`/lobby-day/events/${id}`),

  getAttendees: (eventId: string) =>
    api.get<LobbyDayAttendeeDto[]>(`/lobby-day/events/${eventId}/attendees`),

  addAttendees: (eventId: string, contactIds: string[]) =>
    api.post<AddAttendeesResult>(`/lobby-day/events/${eventId}/attendees`, { contactIds }),

  removeAttendee: (eventId: string, attendeeId: string) =>
    api.delete(`/lobby-day/events/${eventId}/attendees/${attendeeId}`),

  rotateAttendeeToken: (eventId: string, attendeeId: string) =>
    api.post<{ accessToken: string }>(`/lobby-day/events/${eventId}/attendees/${attendeeId}/rotate-token`),

  revokeAttendeeToken: (eventId: string, attendeeId: string, body: RevokeCanvasserGrantRequest) =>
    api.post<void>(`/lobby-day/events/${eventId}/attendees/${attendeeId}/revoke-token`, body),

  setDeliveryMode: (eventId: string, attendeeId: string, mode: AttendeeDeliveryMode) =>
    api.put<void>(`/lobby-day/events/${eventId}/attendees/${attendeeId}/delivery-mode`, { mode }),

  getPrintPackets: (eventId: string, modes?: AttendeeDeliveryMode[]) =>
    api.get<EventPrintPacketsDto>(`/lobby-day/events/${eventId}/print-packets`, {
      params: modes && modes.length ? { modes: modes.join(',') } : undefined,
    }),

  createAsk: (eventId: string, data: SaveLobbyDayAskRequest) =>
    api.post<LobbyDayAskDto>(`/lobby-day/events/${eventId}/asks`, data),

  createAskFromTemplate: (eventId: string, templateId: string) =>
    api.post<LobbyDayAskDto>(`/lobby-day/events/${eventId}/asks/from-template/${templateId}`),

  updateAsk: (eventId: string, askId: string, data: SaveLobbyDayAskRequest) =>
    api.put<LobbyDayAskDto>(`/lobby-day/events/${eventId}/asks/${askId}`, data),

  deleteAsk: (eventId: string, askId: string) =>
    api.delete(`/lobby-day/events/${eventId}/asks/${askId}`),

  getSchedule: (eventId: string) =>
    api.get<LobbyScheduleDto>(`/lobby-day/events/${eventId}/schedule`),

  saveSchedule: (eventId: string, data: SaveLobbyScheduleRequest) =>
    api.put<LobbyScheduleDto>(`/lobby-day/events/${eventId}/schedule`, data),

  getMeetingPacket: (meetingId: string) =>
    api.get<LobbyPacketDto>(`/lobby-day/meetings/${meetingId}/packet`),

  getOutreach: (eventId: string) =>
    api.get<LegislatorOutreachRowDto[]>(`/lobby-day/events/${eventId}/outreach`),

  saveOutreach: (eventId: string, legislatorId: string, data: SaveOutreachRequest) =>
    api.put(`/lobby-day/events/${eventId}/outreach/${legislatorId}`, data),

  getLogistics: (eventId: string) =>
    api.get<LogisticsDto>(`/lobby-day/events/${eventId}/logistics`),

  saveLogistics: (eventId: string, data: SaveLogisticsRequest) =>
    api.put(`/lobby-day/events/${eventId}/logistics`, data),

  getDebrief: (eventId: string) =>
    api.get<EventDebriefDto>(`/lobby-day/events/${eventId}/debrief`),

  recordOutcome: (meetingId: string, data: RecordOutcomeRequest) =>
    api.put(`/lobby-day/meetings/${meetingId}/outcome`, data),

  attachMeetingAsk: (meetingId: string, data: AttachMeetingAskRequest) =>
    api.put(`/lobby-day/meetings/${meetingId}/ask`, data),

  setAttendance: (meetingId: string, assignmentId: string, status: AttendanceStatus) =>
    api.put<{ assignmentId: string; attendance: AttendanceStatus }>(
      `/lobby-day/meetings/${meetingId}/assignments/${assignmentId}/attendance`, { status }),

  setAssignmentRole: (meetingId: string, assignmentId: string, role: MeetingRole) =>
    api.put<{ meetingId: string; assignmentId: string; role: MeetingRole }>(
      `/lobby-day/meetings/${meetingId}/assignments/${assignmentId}/role`, { role }),

  tagMeStaff: (meetingId: string) =>
    api.put<MeetingStaffDto[]>(`/lobby-day/meetings/${meetingId}/staff/me`),

  untagMeStaff: (meetingId: string) =>
    api.delete<MeetingStaffDto[]>(`/lobby-day/meetings/${meetingId}/staff/me`),

  getDayOfLeads: (eventId: string) =>
    api.get<DayOfLeadDto[]>(`/lobby-day/events/${eventId}/day-of/leads`),

  createFollowUpList: (eventId: string, data: CreateFollowUpListRequest) =>
    api.post<CreateFollowUpListResponse>(`/lobby-day/events/${eventId}/follow-up-lists`, data),

  getReconciliation: (eventId: string) =>
    api.get<ReconciliationDto>(`/lobby-day/events/${eventId}/reconciliation`),

  saveReconciliationVotes: (eventId: string, data: SaveVotesRequest) =>
    api.put<SaveVotesResponse>(`/lobby-day/events/${eventId}/reconciliation/votes`, data),

  applyReconciliation: (eventId: string) =>
    api.post<ApplyReconciliationResponse>(`/lobby-day/events/${eventId}/reconciliation/apply`),

  // ---------- Recruiting (Phase 1) ----------

  getTargets: (eventId: string) =>
    api.get<TargetCoverageDto[]>(`/lobby-day/events/${eventId}/targets`),

  addTarget: (eventId: string, data: AddTargetRequest) =>
    api.post<{ targetId: string }>(`/lobby-day/events/${eventId}/targets`, data),

  updateTarget: (eventId: string, targetId: string, data: UpdateTargetRequest) =>
    api.put(`/lobby-day/events/${eventId}/targets/${targetId}`, data),

  removeTarget: (eventId: string, targetId: string) =>
    api.delete(`/lobby-day/events/${eventId}/targets/${targetId}`),

  setEventTargetAll: (eventId: string, data: SetTargetAllRequest) =>
    api.put(`/lobby-day/events/${eventId}/target-all`, data),

  setMemberTarget: (eventId: string, targetId: string, data: SetMemberTargetRequest) =>
    api.put(`/lobby-day/events/${eventId}/targets/${targetId}/is-target`, data),

  // ---------- Re-invite (Phase 1) ----------

  getPriorEvents: (eventId: string) =>
    api.get<PriorEventDto[]>(`/lobby-day/events/${eventId}/reinvite/prior-events`),

  getPriorEventAttendees: (eventId: string, priorEventId: string, params: { page: number; pageSize?: number }) =>
    api.get<PaginatedResponse<ContactSearchResult>>(
      `/lobby-day/events/${eventId}/reinvite/prior-events/${priorEventId}/attendees`, { params }),

  // ---------- Bill linking + roll-call auto-fill (LegiScan Slice 2) ----------

  searchBills: (eventId: string, askId: string, q: string, sessionId?: number) =>
    api.get<BillSearchCandidate[]>(
      `/lobby-day/events/${eventId}/asks/${askId}/bill-search`,
      { params: { q, ...(sessionId != null ? { sessionId } : {}) } }),

  getBillSessions: (eventId: string) =>
    api.get<BillSession[]>(`/lobby-day/events/${eventId}/bill-sessions`).then((r) => r.data),

  linkBill: (eventId: string, askId: string, body: LinkBillRequest) =>
    api.post<AskBill>(`/lobby-day/events/${eventId}/asks/${askId}/bills`, body),

  unlinkBill: (eventId: string, askId: string, askBillId: string) =>
    api.delete(`/lobby-day/events/${eventId}/asks/${askId}/bills/${askBillId}`),

  refreshBills: (eventId: string, askId: string) =>
    api.post<AskBill[]>(`/lobby-day/events/${eventId}/asks/${askId}/bills/refresh`),

  fillVotes: (eventId: string, askId: string) =>
    api.post<{ jobId: string }>(`/lobby-day/events/${eventId}/asks/${askId}/fill-votes`),
}

/** Ask templates (LW-10) -- reusable asks saved off an event, recalled onto a new one. */
export const lobbyAskTemplateApi = {
  list: () => api.get<AskTemplateDto[]>('/lobby-day/ask-templates'),
  save: (data: SaveAskTemplateRequest) =>
    api.post<AskTemplateDto>('/lobby-day/ask-templates', data),
  rename: (templateId: string, data: RenameAskTemplateRequest) =>
    api.put<AskTemplateDto>(`/lobby-day/ask-templates/${templateId}`, data),
  delete: (templateId: string) =>
    api.delete(`/lobby-day/ask-templates/${templateId}`),
}

/** LD-T4-5 packet PDF attachments — organizer-side CRUD + staff download href. */
export const lobbyAttachmentApi = {
  list: (eventId: string) =>
    api.get<LobbyAttachmentList>(`/lobby-day/events/${eventId}/attachments`),

  /** multipart; timeout 0 like uploadFile — a 10 MB PDF on hotel wifi can outlast API_TIMEOUT_MS. */
  upload: (eventId: string, file: File, title?: string, askId?: string) => {
    const form = new FormData()
    form.append('file', file)
    if (title) form.append('title', title)
    if (askId) form.append('askId', askId)
    return api.post<LobbyAttachment>(`/lobby-day/events/${eventId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    })
  },

  update: (eventId: string, attachmentId: string, body: { title: string; askId?: string | null }) =>
    api.put<LobbyAttachment>(`/lobby-day/events/${eventId}/attachments/${attachmentId}`, body),

  remove: (eventId: string, attachmentId: string) =>
    api.delete(`/lobby-day/events/${eventId}/attachments/${attachmentId}`),

  /** A plain href, not an axios call: the browser downloads it with the auth cookie attached. */
  fileUrl: (eventId: string, attachmentId: string) =>
    `/api/lobby-day/events/${eventId}/attachments/${attachmentId}/file`,
}

export const legislatorApi = {
  getAll: (params?: { chamber?: string; level?: string; search?: string; includeInactive?: boolean; state?: string }) =>
    api.get<LegislatorDto[]>('/legislators', { params }),

  getById: (id: string) =>
    api.get<LegislatorDto>(`/legislators/${id}`),

  create: (data: SaveLegislatorRequest) =>
    api.post<LegislatorDto>('/legislators', data),

  update: (id: string, data: SaveLegislatorRequest) =>
    api.put<LegislatorDto>(`/legislators/${id}`, data),

  delete: (id: string) =>
    api.delete(`/legislators/${id}`),

  import: (rows: SaveLegislatorRequest[]) =>
    api.post<ImportLegislatorsResult>('/legislators/import', { rows }),

  constituentCounts: (districts: { chamber: string; district: string; state: string }[]) =>
    api.post<ConstituentCountRow[]>('/legislators/constituent-counts', { districts }),

  sync: (body: SyncLegislatorsRequest) =>
    api.post<{ jobId: string }>('/legislators/sync', body),

  // Running notes (L13). Unwrapped with `.then((r) => r.data)` -- an intentional style
  // deviation from the rest of this group; the useLegislatorNotes/useAddLegislatorNote/
  // useDeleteLegislatorNote hooks in useLobbyDay.ts return these promises directly.
  getNotes: (legislatorId: string) =>
    api.get<LegislatorNoteDto[]>(`/legislators/${legislatorId}/notes`).then((r) => r.data),
  addNote: (legislatorId: string, body: CreateLegislatorNoteRequest) =>
    api.post<LegislatorNoteDto>(`/legislators/${legislatorId}/notes`, body).then((r) => r.data),
  deleteNote: (legislatorId: string, noteId: string) =>
    api.delete(`/legislators/${legislatorId}/notes/${noteId}`).then((r) => r.data),
}

/** Anonymous attendee endpoints — token in the URL, no auth header needed. */
export const lobbyAttendeeApi = {
  getSchedule: (token: string) =>
    api.get<AttendeeScheduleDto>(`/lobby/attendee/${token}/schedule`),

  /** GI-10 attendee link self-resend. Anonymous; always 202 with a constant body, whether or
   *  not the address is registered. Never branch on the response. */
  resendLink: (email: string) =>
    api.post<LinkResendAccepted>('/grant/resend-link', { email }).then((r) => r.data),

  getPacket: (token: string, meetingId: string) =>
    api.get<LobbyPacketDto>(`/lobby/attendee/${token}/meetings/${meetingId}/packet`),

  /** Token-in-URL download href for an attendee's packet document (Q4 = A). */
  attachmentUrl: (token: string, attachmentId: string) =>
    `/api/lobby/attendee/${token}/attachments/${attachmentId}`,

  /** L11 Slice 2 (Task 3/5): grant-cookie-authed self-outcome write. Requires a redeemed
   * `grant_session` cookie (see grantApi.redeem) — carried automatically via withCredentials. */
  recordOutcome: (body: { meetingId: string; response: AskResponse; notes?: string }) =>
    api.post('/lobby/attendee/outcome', body).then(r => r.data as { message: string }),

  /** Grant-cookie-authed self-profile read. Requires a redeemed grant_session cookie (grantApi.redeem). */
  getProfile: () =>
    api.get<AttendeeProfileDto>(`/lobby/attendee/profile`).then(r => r.data),

  /** Grant-cookie-authed self-profile write. Returns a receipt; refetch via getProfile. */
  updateProfile: (body: UpdateAttendeeProfileRequest) =>
    api.put(`/lobby/attendee/profile`, body).then(r => r.data as { message: string }),
}

// ---------------------------------------------------------------------------
// Jobs API (slice 6b worker tier — WI-41/WI-42 background job polling)
// ---------------------------------------------------------------------------
import type { JobStatusDto } from '../types/job'

export const jobsApi = {
  getStatus: (id: string) =>
    api.get<JobStatusDto>(`/jobs/${id}`),
}

// ---------------------------------------------------------------------------
// Reporting API
// ---------------------------------------------------------------------------
import type {
  ReportDefinition,
  ReportRequest,
  ReportResult,
  ReportRunSummary,
} from '../types/reporting'

export const reportsApi = {
  getDefinitions: () =>
    api.get<ReportDefinition[]>('/reports/definitions'),
  preview: (request: ReportRequest) =>
    api.post<ReportResult>('/reports/preview', request),
  createRun: (request: ReportRequest) =>
    api.post<JobEnqueuedDto>('/reports/runs', request),
  listRuns: (take = 50) =>
    api.get<ReportRunSummary[]>(`/reports/runs?take=${take}`),
  getRun: (id: string) =>
    api.get<ReportResult>(`/reports/runs/${id}`),
  deleteRun: (id: string) =>
    api.delete<void>(`/reports/runs/${id}`),
  /**
   * Absolute path for a direct browser download (window.location.assign) — not an axios
   * call, so it never passes through the request interceptor that appends `committeeScope`
   * to every axios request. Build it into the URL explicitly so a CSV download stays scoped
   * when a committee scope is active.
   */
  csvUrl: (runId: string, blockId: string) => {
    const params = new URLSearchParams({ block: blockId })
    const scope = getCommitteeScope()
    if (scope) {
      params.set('committeeScope', scope)
    }
    return `/api/reports/runs/${encodeURIComponent(runId)}/csv?${params.toString()}`
  },
}

// ---------------------------------------------------------------------------
// Communications API (L4 -- outbound email)
// ---------------------------------------------------------------------------
import type {
  CreateEmailRequest,
  CreateEmailResult,
  EmailMessageSummary,
  EmailMessageDetail,
  EmailRecipientRow,
  EmailSourceRef,
  RecipientPreviewResponse,
  PreviewRenderRequest,
  PreviewRenderResponse,
  EmailTemplateDto,
  SaveTemplateRequest,
  SuppressionRow,
  AddSuppressionRequest,
  CommunicationsStatusResponse,
  MergeFieldDef,
  EmailSourceKind,
} from '../types/communications'

// Defense-in-depth belt-and-suspenders alongside the backend fix (EmailRecipientResolver now
// treats `[]` the same as null): normalizes an EmailSourceRef so an empty `deliveryModes` array
// is dropped rather than sent as `deliveryModes: []`. Applied to every call that carries a
// source, so no caller can (re-)introduce the "empty array reads as filter-to-nothing" footgun
// by constructing a `{ deliveryModes: [] }` source, whether that lands in a JSON body
// (create/previewRender) or query params (recipientPreview).
function normalizeSourceRef(source: EmailSourceRef): EmailSourceRef {
  if (!source.deliveryModes || source.deliveryModes.length === 0) {
    const { deliveryModes: _deliveryModes, ...rest } = source
    return rest
  }
  return source
}

export const communicationsApi = {
  create: (data: CreateEmailRequest) =>
    api.post<CreateEmailResult>('/communications/emails', {
      ...data,
      source: normalizeSourceRef(data.source),
    }),

  list: (take = 100) =>
    api.get<EmailMessageSummary[]>('/communications/emails', { params: { take } }),

  get: (id: string) =>
    api.get<EmailMessageDetail>(`/communications/emails/${id}`),

  getRecipients: (id: string, take?: number) =>
    api.get<EmailRecipientRow[]>(`/communications/emails/${id}/recipients`, {
      params: take ? { take } : undefined,
    }),

  // Source's deliveryModes go as repeated query keys (deliveryModes=App&deliveryModes=Paper) so
  // ASP.NET's [FromQuery] array binder can bind them -- an EMPTY/absent key means "all modes"
  // server-side (RecipientPreview's normalizedDeliveryModes), so omit the key entirely rather
  // than sending deliveryModes= with no values.
  recipientPreview: (source: EmailSourceRef) => {
    const normalized = normalizeSourceRef(source)
    return api.get<RecipientPreviewResponse>('/communications/emails/recipient-preview', {
      params: {
        kind: normalized.kind,
        id: normalized.id,
        ...(normalized.deliveryModes ? { deliveryModes: normalized.deliveryModes } : {}),
      },
      paramsSerializer: { indexes: null },
    })
  },

  previewRender: (data: PreviewRenderRequest) =>
    api.post<PreviewRenderResponse>('/communications/emails/preview', {
      ...data,
      source: normalizeSourceRef(data.source),
    }),

  // Templates
  listTemplates: () =>
    api.get<EmailTemplateDto[]>('/communications/templates'),
  getTemplate: (id: string) =>
    api.get<EmailTemplateDto>(`/communications/templates/${id}`),
  createTemplate: (data: SaveTemplateRequest) =>
    api.post<EmailTemplateDto>('/communications/templates', data),
  updateTemplate: (id: string, data: SaveTemplateRequest) =>
    api.put<EmailTemplateDto>(`/communications/templates/${id}`, data),
  deleteTemplate: (id: string) =>
    api.delete(`/communications/templates/${id}`),

  // Suppressions
  listSuppressions: (take?: number) =>
    api.get<SuppressionRow[]>('/communications/suppressions', {
      params: take ? { take } : undefined,
    }),
  addSuppression: (data: AddSuppressionRequest) =>
    api.post<SuppressionRow>('/communications/suppressions', data),
  removeSuppression: (id: string) =>
    api.delete(`/communications/suppressions/${id}`),

  // Composer support
  status: () =>
    api.get<CommunicationsStatusResponse>('/communications/status'),
  mergeFields: (source: EmailSourceKind) =>
    api.get<MergeFieldDef[]>('/communications/merge-fields', { params: { source } }),
}

// ---------------------------------------------------------------------------
// Follow-up API (CF-1)
// ---------------------------------------------------------------------------
import type {
  FollowUpKindDto,
  SaveFollowUpKindRequest,
  RestoreStarterQuestionsResult,
  FollowUpSummaryDto,
  FollowUpItemsParams,
  FollowUpItemsPageDto,
  UpdateFollowUpItemRequest,
  CampaignViewSettingsDto,
  SaveCampaignViewSettingsRequest,
  CutFollowUpRoutesRequest,
  RecutFollowUpBatchRequest,
  FollowUpRoutesParams,
  FollowUpRoutesDto,
  CallBatchesParams,
  FollowUpCallBatchesPageDto,
  FollowUpCallBatchDetailDto,
  CreateCallBatchRequest,
  CreateCallBatchResult,
  AssignCallBatchRequest,
  FollowUpScriptConfigDto,
  SaveFollowUpScriptRequest,
  RecordCallSubmission,
  RecordCallResult,
  ChaseSettingsDto,
  SaveChaseSettingsRequest,
  ChaseSummaryDto,
  PrepareChaseRequest,
  CutChaseBatchRequest,
  ChaseCallListResultDto,
} from '../types/followUp'

export const followUpApi = {
  getKinds: () => api.get<FollowUpKindDto[]>('/follow-up/kinds'),
  createKind: (data: SaveFollowUpKindRequest) => api.post<FollowUpKindDto>('/follow-up/kinds', data),
  updateKind: (id: string, data: SaveFollowUpKindRequest) => api.put(`/follow-up/kinds/${id}`, data),
  deleteKind: (id: string) => api.delete(`/follow-up/kinds/${id}`),
  restoreStarterQuestions: () =>
    api.post<RestoreStarterQuestionsResult>('/surveys/starter-questions/restore'),

  getSummary: (campaignId: string) =>
    api.get<FollowUpSummaryDto>(`/campaigns/${campaignId}/follow-up/summary`),
  getItems: (campaignId: string, params: FollowUpItemsParams) =>
    api.get<FollowUpItemsPageDto>(`/campaigns/${campaignId}/follow-up/items`, { params }),
  updateItem: (campaignId: string, itemId: string, data: UpdateFollowUpItemRequest) =>
    api.patch(`/campaigns/${campaignId}/follow-up/items/${itemId}`, data),

  getViewSettings: (campaignId: string) =>
    api.get<CampaignViewSettingsDto>(`/campaigns/${campaignId}/view-settings`),
  saveViewSettings: (campaignId: string, data: SaveCampaignViewSettingsRequest) =>
    api.put(`/campaigns/${campaignId}/view-settings`, data),

  // CF-1 P2: follow-up driving routes. Cut/recut are 202 { jobId } — completion is polled.
  cutRoutes: (campaignId: string, body: CutFollowUpRoutesRequest) =>
    api.post<JobEnqueuedDto>(`/campaigns/${campaignId}/follow-up/routes/cut`, body),
  recutBatch: (campaignId: string, turfMapId: string, body: RecutFollowUpBatchRequest) =>
    api.post<JobEnqueuedDto>(`/campaigns/${campaignId}/follow-up/routes/batches/${turfMapId}/recut`, body),
  getRoutes: (campaignId: string, params: FollowUpRoutesParams) =>
    api.get<FollowUpRoutesDto>(`/campaigns/${campaignId}/follow-up/routes`, {
      params: { kind: params.kind, customKindId: params.customKindId ?? undefined },
    }),

  // CF-1 P4: call lists + follow-up scripts.
  getScripts: (campaignId: string) =>
    api.get<FollowUpScriptConfigDto[]>(`/campaigns/${campaignId}/follow-up/scripts`),
  saveScript: (campaignId: string, data: SaveFollowUpScriptRequest) =>
    api.put(`/campaigns/${campaignId}/follow-up/scripts`, data),
  createCallBatch: (campaignId: string, data: CreateCallBatchRequest) =>
    api.post<CreateCallBatchResult>(`/campaigns/${campaignId}/follow-up/call-batches`, data),
  getCallBatches: (campaignId: string, params: CallBatchesParams) =>
    api.get<FollowUpCallBatchesPageDto>(`/campaigns/${campaignId}/follow-up/call-batches`, { params }),
  getCallBatch: (campaignId: string, batchId: string) =>
    api.get<FollowUpCallBatchDetailDto>(`/campaigns/${campaignId}/follow-up/call-batches/${batchId}`),
  assignCallBatch: (campaignId: string, batchId: string, data: AssignCallBatchRequest) =>
    api.put(`/campaigns/${campaignId}/follow-up/call-batches/${batchId}/assignment`, data),
  unassignCallBatch: (campaignId: string, batchId: string) =>
    api.delete(`/campaigns/${campaignId}/follow-up/call-batches/${batchId}/assignment`),
  deleteCallBatch: (campaignId: string, batchId: string) =>
    api.delete(`/campaigns/${campaignId}/follow-up/call-batches/${batchId}`),

  // CF-1 P5: chase to vote. Prepare/cut are 202 { jobId } (polled); call-list is 200 { callBatchId }.
  getChaseSettings: (campaignId: string) =>
    api.get<ChaseSettingsDto>(`/campaigns/${campaignId}/follow-up/chase/settings`),
  saveChaseSettings: (campaignId: string, data: SaveChaseSettingsRequest) =>
    api.put<ChaseSettingsDto>(`/campaigns/${campaignId}/follow-up/chase/settings`, data),
  getChaseSummary: (campaignId: string, includeWidened: boolean) =>
    api.get<ChaseSummaryDto>(`/campaigns/${campaignId}/follow-up/chase/summary`, { params: { includeWidened } }),
  prepareChase: (campaignId: string, body: PrepareChaseRequest) =>
    api.post<JobEnqueuedDto>(`/campaigns/${campaignId}/follow-up/chase/prepare`, body),
  cutChaseBatch: (campaignId: string, batchId: string, body: CutChaseBatchRequest) =>
    api.post<JobEnqueuedDto>(`/campaigns/${campaignId}/follow-up/chase/batches/${batchId}/cut`, body),
  chaseCallList: (campaignId: string, batchId: string) =>
    api.post<ChaseCallListResultDto>(`/campaigns/${campaignId}/follow-up/chase/batches/${batchId}/call-list`),
}
